import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	MotifModal,
	MotifModalBody,
	MotifModalHeader,
	MotifIcon,
	MotifProgressLoader,
} from "@ey-xd/motif-react";
import {
	IconoirCheckCircle,
	MotifAlertIcErrorOutline24px,
} from "@ey-xd/motif-icon";
import {
	useLazyReCalculateEntitlementQuery,
	useLazyReadDistributionProcessingQuery,
	useLazyCalculateSettlementQuery,
} from "@/services/distribution/distributionReducers";

import "./ProcessingPopup.scss";

const DistributionProcessingPopup = ({
	showModal,
	distributionId,
	distributionName,
	type,
	onClose,
}: {
	showModal: boolean;
	distributionId: string | undefined;
	distributionName: string;
	type: string;
	onClose: () => void;
}) => {
	const { t } = useTranslation();
	const [uploadStatus, setUploadStatus] = useState("");
	const [showProgress, setShowProgress] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [reCalculateEntitlement] = useLazyReCalculateEntitlementQuery();
	const [calculateSettlement] = useLazyCalculateSettlementQuery();
	const [getDistributionProcessingStatus, { data }] =
		useLazyReadDistributionProcessingQuery();
	const loaderText =
		type === "entitlement"
			? t("pages.distributions.distribution.calculateEntitlementLoaderText")
			: t("pages.distributions.distribution.calculateSettlementLoaderText");
	const title =
		type === "entitlement"
			? t("pages.distributions.distribution.calculateEntitlement")
			: t("pages.distributions.distribution.calculateSettlement");

	const successMessage =
		type === "entitlement"
			? t("pages.distributions.distribution.entitlementSuccessMessage", {
					distributionName: distributionName,
			  })
			: t("pages.distributions.distribution.settlementSuccessMessage", {
					distributionName: distributionName,
			  });

	const errorMessageText =
		type === "entitlement"
			? t("pages.distributions.distribution.entitlementErrorMessage", {
					errorMessage: "",
			  })
			: t("pages.distributions.distribution.settlementErrorMessage", {
					errorMessage: "",
			  });

	useEffect(() => {
		const generateWebjob = async () => {
			if (showModal) {
				setIsLoading(true);
				try {
					const response: any =
						type === "entitlement"
							? await reCalculateEntitlement(distributionId)
							: await calculateSettlement(distributionId);
					setIsLoading(false);
					if (response.data) {
						await getDistributionProcessingStatus(distributionId);
						setShowProgress(true);
						setUploadStatus("inProgress");
					} else {
						const error = response?.error?.data?.message
							? response?.error?.data?.message
							: response?.data?.message;
						setUploadStatus("error");
						setErrorMessage(
							error || t("global.globalNotifications.unExpectedError")
						);
					}
				} catch (error) {
					setIsLoading(false);
					setUploadStatus("error");
					setErrorMessage(t("global.globalNotifications.unExpectedError"));
				}
			}
		};
		generateWebjob();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		distributionId,
		getDistributionProcessingStatus,
		reCalculateEntitlement,
		showModal,
		t,
	]);

	useEffect(() => {
		let intervalId: NodeJS.Timeout | null = null;

		if (
			showProgress &&
			(data?.processingStatus === 1 || data?.processingStatus === 2)
		) {
			intervalId = setInterval(() => {
				getDistributionProcessingStatus(distributionId);
			}, 10000);
		}
		if (showProgress && data?.processingStatus === 3) {
			setUploadStatus("error");
			setErrorMessage(data?.processingStatusMessage);
			setShowProgress(false);
		} else if (showProgress && data?.processingStatus === 0) {
			setUploadStatus("Success");
			setShowProgress(false);
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [showProgress, getDistributionProcessingStatus, data, distributionId]);

	return (
		<MotifModal
			onClose={onClose}
			show={showModal}
			focusTrapOptions={{
				tabbableOptions: {
					displayCheck: "none",
				},
			}}
		>
			<MotifModalHeader
				closeButtonProps={{
					"aria-label": "Custom Close Button aria-label",
					title: "Custom Close Button title",
				}}
			>
				{title}
			</MotifModalHeader>
			<MotifModalBody>
				{isLoading ? (
					<MotifProgressLoader show variant="default" />
				) : (
					<div className="distribution-processing-popup">
						{uploadStatus === "Success" ? (
							<div className="success-message">
								<div className="icon-container">
									<MotifIcon
										iconFunction={IconoirCheckCircle}
										stroke="#4CAF50"
										strokeWidth="1"
										size="28"
									/>
								</div>
								<div className="message-title">
									{successMessage}
								</div>
							</div>
						) : uploadStatus === "error" ? (
							<div className="error-message">
								<div className="icon-container">
									<MotifIcon
										iconFunction={MotifAlertIcErrorOutline24px}
										stroke="#FF3D00"
										strokeWidth="1"
										size="28"
									/>
								</div>
								<div className="message-title">
									{errorMessageText}
								</div>
								<div className="message-description">{errorMessage}</div>
							</div>
						) : (
							<div className="loader-container">
								<span className="loader"></span>
								<div className="import-status">{loaderText}</div>
								{data?.processingStatusMessage && [1,2].includes(data?.processingStatus) && <div className="import-status">{data.processingStatusMessage}</div>}
							</div>
						)}
					</div>
				)}
			</MotifModalBody>
		</MotifModal>
	);
};
export default DistributionProcessingPopup;
